import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import LoadingSpinner from '../components/LoadingSpinner';
import { Product } from '../types';
import API_URL from '../config/api';

const AdminProductsPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [categories, setCategories] = useState<{id: number, name: string, slug: string}[]>([]);
  const [isDeleting, setIsDeleting] = useState<string | number | null>(null);
  const [showAsinImport, setShowAsinImport] = useState(false);
  const [asinInput, setAsinInput] = useState('');
  const [asinCategory, setAsinCategory] = useState('');
  const [asinLoading, setAsinLoading] = useState(false);
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
    }
  }, [navigate]);

  // Fetch products and categories
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('adminToken');
        
        // Fetch categories
        const categoriesResponse = await fetch(`${API_URL}/categories`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!categoriesResponse.ok) {
          throw new Error('Failed to fetch categories');
        }
        
        const categoriesData = await categoriesResponse.json();
        setCategories(categoriesData);
        
        // Fetch products
        let url = `${API_URL}/products`;
        const params = new URLSearchParams();
        
        if (searchQuery) {
          params.append('query', searchQuery);
        }
        
        if (selectedCategory) {
          params.append('category', selectedCategory);
        }
        
        if (params.toString()) {
          url += `?${params.toString()}`;
        }
        
        const productsResponse = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!productsResponse.ok) {
          throw new Error('Failed to fetch products');
        }
        
        const productsData = await productsResponse.json();
        setProducts(productsData);
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [searchQuery, selectedCategory, navigate]);

  const handleDeleteProduct = async (id: string | number) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }
    
    setIsDeleting(id);
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/products/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete product');
      }
      
      // Remove product from state
      setProducts(products.filter(product => product.id !== id));
      setIsDeleting(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete product');
      setIsDeleting(null);
    }
  };

  const handleAsinImport = async (e: React.FormEvent) => {
    e.preventDefault();
    setAsinLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/products/import-asin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ asin: asinInput, category: asinCategory })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Import failed');
      }
      setAsinInput('');
      setAsinCategory('');
      setShowAsinImport(false);
      // Refresh products
      setLoading(true);
      const productsResponse = await fetch(`${API_URL}/products`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const productsData = await productsResponse.json();
      setProducts(productsData);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setAsinLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <SEO
        title="Manage Products | Admin Panel"
        description="Manage products in the GoodCart admin panel"
        url="https://goodcart.org/admin/products"
      />
      
      <div className="container-custom">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 bg-gray-800 text-white">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
              <h1 className="text-2xl font-bold">Manage Products</h1>
              <div className="flex space-x-4 flex-wrap gap-y-2">
                <Link
                  to="/admin"
                  className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                >
                  Back to Dashboard
                </Link>
                <button
                  onClick={() => setShowAsinImport(!showAsinImport)}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  {showAsinImport ? 'Cancel Import' : 'Import by ASIN'}
                </button>
                <Link
                  to="/admin/products/new"
                  className="px-4 py-2 bg-brand-green-600 text-white rounded hover:bg-brand-green-700 transition-colors"
                >
                  Add New Product
                </Link>
              </div>
            </div>
          </div>
          
          {error && (
            <div className="p-4 bg-red-50 text-red-700 border-b border-red-100">
              {error}
            </div>
          )}

          {showAsinImport && (
            <div className="p-6 border-b border-gray-200 bg-blue-50">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Import product by ASIN</h2>
              <form onSubmit={handleAsinImport} className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 items-end">
                <div className="flex-1 w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-1">ASIN *</label>
                  <input
                    type="text"
                    value={asinInput}
                    onChange={(e) => setAsinInput(e.target.value.toUpperCase())}
                    placeholder="e.g. B08N5WRWNW"
                    required
                    maxLength={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="md:w-64 w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    value={asinCategory}
                    onChange={(e) => setAsinCategory(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.slug}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={asinLoading}
                  className={`px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors ${asinLoading ? 'opacity-75' : ''}`}
                >
                  {asinLoading ? 'Importing...' : 'Import'}
                </button>
              </form>
              <p className="text-sm text-gray-500 mt-2">
                Enter a 10-character ASIN and select a category. The product will be created with placeholder data that you can edit afterward.
              </p>
            </div>
          )}
          
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
              <div className="flex-1">
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                  Search Products
                </label>
                <input
                  type="text"
                  id="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or description..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green-500"
                />
              </div>
              
              <div className="md:w-64">
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Category
                </label>
                <select
                  id="category"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green-500"
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.slug}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          {loading ? (
            <div className="p-12 flex justify-center">
              <LoadingSpinner />
            </div>
          ) : products.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-500 mb-4">No products found</p>
              <Link
                to="/admin/products/new"
                className="btn btn-primary"
              >
                Add Your First Product
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ASIN
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            <img
                              className="h-10 w-10 rounded-md object-cover"
                              src={product.image}
                              alt={product.name}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = `https://placehold.co/100x100?text=${product.name.charAt(0)}`;
                              }}
                            />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {product.name}
                            </div>
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {product.description.length > 50 
                                ? `${product.description.substring(0, 50)}...` 
                                : product.description}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          {product.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        ${product.price.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product.asin ? (
                          <a
                            href={`https://www.amazon.com/dp/${product.asin}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800"
                          >
                            {product.asin}
                          </a>
                        ) : (
                          <span className="text-gray-400">·</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <Link
                            to={`/admin/products/edit/${product.id}`}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            Edit
                          </Link>
                          <button
                            onClick={() => handleDeleteProduct(product.id)}
                            disabled={isDeleting === product.id}
                            className={`text-red-600 hover:text-red-900 ${isDeleting === product.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {isDeleting === product.id ? 'Deleting...' : 'Delete'}
                          </button>
                          <Link
                            to={`/products/${product.id}`}
                            target="_blank"
                            className="text-gray-600 hover:text-gray-900"
                          >
                            View
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminProductsPage;
